import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { makeBranchApi } from "../../api/branchApi";
import { PageHeader, Btn, LoadingSpinner } from "../../components/ui";

export default function BranchDisplayPage() {
  const { branchId } = useParams();
  const api = makeBranchApi(branchId);

  const [slug,    setSlug]    = useState(null);
  const [zones,   setZones]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied,  setCopied]  = useState(null);

  useEffect(() => {
    Promise.all([api.getSettings(), api.listZones()])
      .then(([sr, zr]) => {
        setSlug(sr.data?.facility_slug || null);
        setZones(zr.data?.results || zr.data || []);
      }).catch(() => {})
      .finally(() => setLoading(false));
  }, [branchId]);

  const boardUrl = (zoneId = null) => {
    const base = `${window.location.origin}/display/${slug || "(your-slug)"}`;
    return zoneId ? `${base}?zone=${zoneId}` : base;
  };

  const copy = (url, key) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div style={styles.root}>
      <PageHeader
        title="Display Board"
        subtitle="Open these URLs on any TV or monitor. Each board updates live and plays audio announcements."
      />

      {loading ? <LoadingSpinner /> : (
        <>
          <div style={styles.urlCard}>
            <div style={styles.urlLeft}>
              <span style={{ fontSize: "28px", flexShrink: 0 }}>📺</span>
              <div>
                <div style={styles.urlTitle}>Full Branch Board</div>
                <div style={styles.urlDesc}>Shows all zones and counters on one screen</div>
                <a href={boardUrl()} target="_blank" rel="noopener noreferrer" style={styles.urlText}>{boardUrl()}</a>
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <Btn onClick={() => window.open(boardUrl(), "_blank")}>Open ↗</Btn>
              <Btn variant="ghost" onClick={() => copy(boardUrl(), "main")}>{copied === "main" ? "Copied ✓" : "Copy URL"}</Btn>
            </div>
          </div>

          {/* Kiosk URL */}
          <div style={styles.urlCard}>
            <div style={styles.urlLeft}>
              <span style={{ fontSize: "28px", flexShrink: 0 }}>🖥️</span>
              <div>
                <div style={styles.urlTitle}>Kiosk</div>
                <div style={styles.urlDesc}>Open on the tablet or touchscreen where patients get queue numbers</div>
                <a href={`${window.location.origin}/kiosk/${slug || ""}`} target="_blank" rel="noopener noreferrer" style={styles.urlText}>{`${window.location.origin}/kiosk/${slug || "(your-slug)"}`}</a>
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <Btn onClick={() => window.open(`${window.location.origin}/kiosk/${slug || ""}`, "_blank")}>Open ↗</Btn>
              <Btn variant="ghost" onClick={() => copy(`${window.location.origin}/kiosk/${slug || ""}`, "kiosk")}>{copied === "kiosk" ? "Copied ✓" : "Copy URL"}</Btn>
            </div>
          </div>

          {zones.length > 0 && (
            <>
              <div style={styles.sectionHeader}>
                <div style={styles.sectionTitle}>Zone-Specific Boards</div>
                <div style={styles.sectionDesc}>Use these URLs to show only one zone on a specific TV — useful when each block or floor has its own screen.</div>
              </div>
              {zones.map(zone => (
                <div key={zone.id} style={styles.zoneUrlCard}>
                  <div style={styles.urlLeft}>
                    <span style={{ fontSize: "20px" }}>📟</span>
                    <div>
                      <div style={{ fontSize: "14px", fontWeight: "600", color: "#E2E8F0" }}>{zone.name}</div>
                      <a href={boardUrl(zone.id)} target="_blank" rel="noopener noreferrer" style={{ ...styles.urlText, fontSize: "11px" }}>{boardUrl(zone.id)}</a>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <Btn size="sm" onClick={() => window.open(boardUrl(zone.id), "_blank")}>Open ↗</Btn>
                    <Btn size="sm" variant="ghost" onClick={() => copy(boardUrl(zone.id), zone.id)}>{copied === zone.id ? "Copied ✓" : "Copy"}</Btn>
                  </div>
                </div>
              ))}
            </>
          )}

          <div style={styles.featuresSection}>
            <div style={styles.sectionTitle}>What the Display Board Shows</div>
            <div style={styles.featuresGrid}>
              {[
                ["🎫", "Currently Serving", "Ticket number at each active counter"],
                ["👤", "Staff Names", "Optionally shows who is serving at each counter"],
                ["⏭️", "Up Next", "Next patients waiting, sorted by priority"],
                ["📊", "Queue Stats", "Waiting, serving, and completed today"],
                ["⏱️", "Avg Wait Time", "Estimated wait per station based on recent history"],
                ["📢", "Announcement Banner", "Scrolling notice text"],
                ["🔊", "Audio Announcements", "Chime + ticket number + station name on every call"],
                ["⚡", "Live Updates", "Instant refresh when any ticket is called"],
              ].map(([icon, title, desc]) => (
                <div key={title} style={styles.feature}>
                  <span style={{ fontSize: "20px" }}>{icon}</span>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: "600", color: "#E2E8F0" }}>{title}</div>
                    <div style={{ fontSize: "12px", color: "#475569" }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  root:           { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "860px" },
  urlCard:        { background: "rgba(15,23,42,0.6)", border: "1px solid rgba(37,99,235,0.2)", borderRadius: "14px", padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", marginBottom: "16px" },
  urlLeft:        { display: "flex", gap: "16px", alignItems: "flex-start", flex: 1, minWidth: 0 },
  urlTitle:       { fontSize: "15px", fontWeight: "700", color: "#E2E8F0", marginBottom: "2px" },
  urlDesc:        { fontSize: "12px", color: "#475569", marginBottom: "6px" },
  urlText:        { fontSize: "12px", color: "#2563EB", fontFamily: "monospace", wordBreak: "break-all" },
  sectionHeader:  { marginBottom: "12px", marginTop: "8px" },
  sectionTitle:   { fontSize: "11px", fontWeight: "700", color: "#475569", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: "6px" },
  sectionDesc:    { fontSize: "12px", color: "#475569" },
  zoneUrlCard:    { background: "rgba(30,41,59,0.4)", border: "1px solid rgba(37,99,235,0.1)", borderRadius: "10px", padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", marginBottom: "8px" },
  featuresSection:{ marginTop: "28px" },
  featuresGrid:   { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginTop: "14px" },
  feature:        { display: "flex", gap: "10px", alignItems: "flex-start", background: "rgba(15,23,42,0.4)", border: "1px solid rgba(37,99,235,0.08)", borderRadius: "10px", padding: "12px 14px" },
};
